import { computeInvoiceTotals, InvoiceItemKind, type InvoiceItemKind as ItemKind } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Receipt, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Field, PageHeader, Skeleton, Spinner, useToast } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';
import { fmtEgp, fmtNumber } from '../../lib/format';
import type { Customer } from '../customers/CustomersPage';
import { catName, type ExpenseRow } from '../expenses/types';
import type { TripRow } from '../trips/types';
import type { InvoiceDetail } from './types';

interface ItemDraft {
  key: string;
  kind: ItemKind;
  description: string;
  quantity: string;
  unitPriceEgp: string;
  expenseId: string | null;
}

const key = () => Math.random().toString(36).slice(2);
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

/** /invoices/new (optionally ?tripId=) and /invoices/:id/edit (drafts only). */
export function InvoiceFormPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = !!id;
  const settings = useQuery({ queryKey: ['settings'], enabled: !isEdit, queryFn: () => api<{ settings: { vatRatePct: number } }>('/settings') });

  const [customerId, setCustomerId] = useState('');
  const [tripId, setTripId] = useState(params.get('tripId') ?? '');
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState(plusDays(30));
  const [taxRatePct, setTaxRatePct] = useState('14');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([{ key: key(), kind: 'FREIGHT', description: '', quantity: '1', unitPriceEgp: '', expenseId: null }]);

  const existing = useQuery({ queryKey: ['invoice', id], enabled: isEdit, queryFn: () => api<InvoiceDetail>(`/invoices/${id}`) });
  const customers = useQuery({ queryKey: ['customers-lite'], queryFn: () => api<Paginated<Customer>>('/customers?pageSize=200').then((r) => r.data) });
  const trips = useQuery({ queryKey: ['trips-for-invoice', customerId], enabled: !!customerId, queryFn: () => api<Paginated<TripRow>>(`/trips${qs({ customerId, pageSize: 100, sort: 'createdAt:desc' })}`).then((r) => r.data) });
  const prefillTrip = useQuery({ queryKey: ['trip', tripId], enabled: !isEdit && !!tripId && !customerId, queryFn: () => api<TripRow>(`/trips/${tripId}`) });
  const uninvoiced = useQuery({ queryKey: ['expenses', 'uninvoiced', tripId], enabled: !!tripId, queryFn: () => api<Paginated<ExpenseRow>>(`/expenses${qs({ uninvoiced: true, tripId, pageSize: 100 })}`).then((r) => r.data) });

  useEffect(() => {
    if (prefillTrip.data?.customer?.id) setCustomerId(prefillTrip.data.customer.id);
  }, [prefillTrip.data]);
  useEffect(() => {
    if (settings.data?.settings?.vatRatePct != null) setTaxRatePct(String(settings.data.settings.vatRatePct));
  }, [settings.data]);
  useEffect(() => {
    const inv = existing.data;
    if (!inv) return;
    setCustomerId(inv.customer.id);
    setTripId(inv.trip?.id ?? '');
    setIssueDate(inv.issueDate.slice(0, 10));
    setDueDate(inv.dueDate?.slice(0, 10) ?? '');
    setTaxRatePct(String(inv.taxRatePct));
    setNotes(inv.notes ?? '');
    setItems(inv.items.map((i) => ({ key: key(), kind: i.kind, description: i.description, quantity: String(i.quantity), unitPriceEgp: String(i.unitPriceEgp), expenseId: i.expenseId })));
  }, [existing.data]);

  const totals = useMemo(() => computeInvoiceTotals(items.map((i) => ({ quantity: Number(i.quantity) || 0, unitPriceEgp: Number(i.unitPriceEgp) || 0 })), Number(taxRatePct) || 0), [items, taxRatePct]);
  const valid = !!customerId && items.length > 0 && items.every((i) => i.description.trim() && Number(i.quantity) > 0 && Number(i.unitPriceEgp) >= 0);
  const selectedTrip = trips.data?.find((tr) => tr.id === tripId);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        customerId,
        tripId: tripId || null,
        issueDate,
        dueDate: dueDate || null,
        taxRatePct: Number(taxRatePct),
        notes: notes || null,
        items: items.map((i) => ({ kind: i.kind, description: i.description.trim(), quantity: Number(i.quantity), unitPriceEgp: Number(i.unitPriceEgp), expenseId: i.expenseId })),
      };
      return isEdit ? api<InvoiceDetail>(`/invoices/${id}`, { method: 'PATCH', json: body }) : api<InvoiceDetail>('/invoices', { method: 'POST', json: body });
    },
    onSuccess: (inv) => {
      toast.success(t('common.saved'));
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['invoice', inv.id] });
      nav(`/invoices/${inv.id}`);
    },
    onError: toast.error,
  });

  const setItem = (k: string, patch: Partial<ItemDraft>) => setItems((p) => p.map((i) => (i.key === k ? { ...i, ...patch } : i)));
  const addExpense = (e: ExpenseRow) => setItems((p) => [...p, { key: key(), kind: 'REBILLED_EXPENSE', description: `${catName(e.category, i18n.language)} — ${e.number}${e.notes ? ` · ${e.notes}` : ''}`, quantity: '1', unitPriceEgp: String(e.amountEgp), expenseId: e.id }]);
  const addDistance = () => selectedTrip?.distanceKm && setItems((p) => [...p, { key: key(), kind: 'DISTANCE', description: `${t('invoices.kinds.DISTANCE')} ${selectedTrip.number}`, quantity: String(Number(selectedTrip.distanceKm)), unitPriceEgp: '', expenseId: null }]);

  if (isEdit && (existing.isLoading || !existing.data)) return <Skeleton className="h-96" />;
  if (isEdit && existing.data?.status !== 'DRAFT') return <div className="card text-sm text-text-muted">{t('invoices.onlyDraftsEditable')}</div>;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={isEdit ? `${t('common.edit')} ${existing.data?.number ?? ''}` : t('invoices.new')} actions={<button className="btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : <Save size={16} />} {t('common.save')}</button>} />
      <div className="grid gap-4 lg:grid-cols-3">
        <section className="card flex flex-col gap-4 lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('invoices.customer')} required>
              {(fid) => (
                <select id={fid} className="input" value={customerId} onChange={(e) => { setCustomerId(e.target.value); setTripId(''); }}>
                  <option value="">—</option>
                  {(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </Field>
            <Field label={t('invoices.trip')} hint={t('invoices.tripHint')}>
              {(fid) => (
                <select id={fid} className="input" value={tripId} disabled={!customerId} onChange={(e) => setTripId(e.target.value)}>
                  <option value="">—</option>
                  {(trips.data ?? []).map((tr) => <option key={tr.id} value={tr.id}>{tr.number} · {t(`trips.statuses.${tr.status}`)}{tr.distanceKm ? ` · ${fmtNumber(tr.distanceKm, i18n.language, 1)} ${t('units.km')}` : ''}</option>)}
                </select>
              )}
            </Field>
            <Field label={t('invoices.issueDate')} required>{(fid) => <input id={fid} type="date" dir="ltr" className="input" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />}</Field>
            <Field label={t('invoices.dueDate')}>{(fid) => <input id={fid} type="date" dir="ltr" className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />}</Field>
            <Field label={t('invoices.taxRate')}>{(fid) => <input id={fid} type="number" min={0} max={100} step={0.01} dir="ltr" className="input" value={taxRatePct} onChange={(e) => setTaxRatePct(e.target.value)} />}</Field>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-bold text-text-2">{t('invoices.items')}</h2>
              <div className="flex gap-2">
                {selectedTrip?.distanceKm ? <button type="button" className="btn-secondary" onClick={addDistance}><Plus size={14} /> {t('invoices.addDistanceLine')}</button> : null}
                <button type="button" className="btn-secondary" onClick={() => setItems((p) => [...p, { key: key(), kind: 'OTHER', description: '', quantity: '1', unitPriceEgp: '', expenseId: null }])}><Plus size={14} /> {t('invoices.addItem')}</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-text-muted">
                  <tr><th className="pb-1 text-start">{t('invoices.itemKind')}</th><th className="pb-1 text-start">{t('invoices.itemDescription')}</th><th className="pb-1 text-end">{t('invoices.qty')}</th><th className="pb-1 text-end">{t('invoices.unitPrice')}</th><th className="pb-1 text-end">{t('invoices.lineTotal')}</th><th /></tr>
                </thead>
                <tbody>
                  {items.map((i, idx) => (
                    <tr key={i.key} className="border-t border-border-soft">
                      <td className="py-2 pe-2">
                        <select className="input !min-h-9 w-36" value={i.kind} aria-label={t('invoices.itemKind')} onChange={(e) => setItem(i.key, { kind: e.target.value as ItemKind })}>
                          {InvoiceItemKind.map((k) => <option key={k} value={k}>{t(`invoices.kinds.${k}`)}</option>)}
                        </select>
                      </td>
                      <td className="py-2 pe-2"><input className="input !min-h-9 min-w-52" value={i.description} aria-label={t('invoices.itemDescription')} onChange={(e) => setItem(i.key, { description: e.target.value })} /></td>
                      <td className="py-2 pe-2"><input type="number" min={0.01} step={0.01} dir="ltr" className="input !min-h-9 w-24 text-end" value={i.quantity} aria-label={t('invoices.qty')} onChange={(e) => setItem(i.key, { quantity: e.target.value })} /></td>
                      <td className="py-2 pe-2"><input type="number" min={0} step={0.01} dir="ltr" className="input !min-h-9 w-32 text-end" value={i.unitPriceEgp} aria-label={t('invoices.unitPrice')} onChange={(e) => setItem(i.key, { unitPriceEgp: e.target.value })} /></td>
                      <td className="py-2 text-end font-semibold tabular-nums">{fmtEgp(totals.lines[idx], i18n.language)}</td>
                      <td className="py-2 ps-2"><button type="button" className="btn-ghost size-9 !p-0 text-danger-fg" aria-label={t('common.delete')} onClick={() => setItems((p) => p.filter((x) => x.key !== i.key))}><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {tripId && uninvoiced.data?.length ? (
            <div className="rounded-xl border border-border-soft bg-surface-2 p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-text-2"><Receipt size={14} /> {t('invoices.rebillableExpenses')}</div>
              <ul className="flex flex-col gap-1 text-sm">
                {uninvoiced.data.filter((e) => !items.some((i) => i.expenseId === e.id)).map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-2">
                    <span>{catName(e.category, i18n.language)} · <span dir="ltr">{e.number}</span> · <strong>{fmtEgp(e.amountEgp, i18n.language)}</strong></span>
                    <button type="button" className="btn-secondary !min-h-8" onClick={() => addExpense(e)}><Plus size={14} /> {t('common.add')}</button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <Field label={t('invoices.notes')}>{(fid) => <textarea id={fid} className="input min-h-20" value={notes} onChange={(e) => setNotes(e.target.value)} />}</Field>
        </section>

        <section className="card h-fit">
          <h2 className="mb-3 text-sm font-bold text-text-2">{t('invoices.totals')}</h2>
          <dl className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between"><dt className="text-text-muted">{t('invoices.subtotal')}</dt><dd className="tabular-nums">{fmtEgp(totals.subtotalEgp, i18n.language)}</dd></div>
            <div className="flex justify-between"><dt className="text-text-muted">{t('invoices.vat')} ({taxRatePct}%)</dt><dd className="tabular-nums">{fmtEgp(totals.taxEgp, i18n.language)}</dd></div>
            <div className="flex justify-between border-t border-border pt-2 text-lg font-bold"><dt>{t('invoices.total')}</dt><dd className="tabular-nums">{fmtEgp(totals.totalEgp, i18n.language)}</dd></div>
          </dl>
        </section>
      </div>
    </div>
  );
}
